import { ReferencedEntry } from 'contentful';
import React, { AllHTMLAttributes } from 'react';

import { Feature, FeatureComparisonAssembly, SubscriptionPlan } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Blurb } from 'src/components/Contentful/Blurb';
import { FlexTable, FlexTableCell, FlexTableRow } from 'src/components/FlexTable/FlexTable';
import { useSubscriptionPlan } from 'src/components/SubscriptionPlan/UseSubscriptionPlan';
import { componentKey, getField } from 'src/utils/EntryUtil';
import { useFeatureMap } from './UseFeatureMap';

import styles from './FeatureComparisonModule.scss';

interface HasEntry<T> {
  entry?: ReferencedEntry<T>;
}

export const FeatureComparisonModule: React.FC<HasEntry<FeatureComparisonAssembly> & Omit<AllHTMLAttributes<HTMLElement>, 'children'>> = ({ entry, ...props }) => {
  const items = getField(entry, 'items', []);
  const features = getField(entry, 'featureTopics', []);
  const { getFeatureForComparisonItem } = useFeatureMap(items);

  if (!items.length || !features.length) return null;
  return (
    <div {...props}>
      <FlexTable>
        <FeatureTableHeader items={items} />
        {features.map((feature, index) => (
          <FlexTableRow key={componentKey(feature, index)}>
            <FlexTableCell>{getField(feature, 'title')}</FlexTableCell>
            {items.map((item, index) => (
              <FlexTableCell key={componentKey(item, index)}>
                <FeatureCellContent entry={getFeatureForComparisonItem(feature, item)} />
              </FlexTableCell>
            ))}
          </FlexTableRow>
        ))}
      </FlexTable>
    </div>
  );
};

export const FeatureCellContent: React.FC<HasEntry<Feature>> = ({ entry }) => {
  if (!entry) return null;
  const value = getField(entry, 'value');
  return value
    ? <Blurb entry={value} field="blurb" />
    : <i className={styles.checkmark} />;
};

interface IFeatureTableHeader {
  items: ReadonlyArray<ReferencedEntry<SubscriptionPlan>>;
}

export const FeatureTableHeader: React.FC<IFeatureTableHeader> = ({ items }) => {
  const { getMonthlyPrice } = useSubscriptionPlan();
  return (
    <FlexTableRow isHeader className={styles.header}>
      <FlexTableCell />
      {items.map((item, index) => {
        const planPrice = getMonthlyPrice(item);
        return (
          <FlexTableCell
            className={styles.headerCell}
            key={componentKey(item, index)}
          >
            <h3>{getField(item, 'displayName')}</h3>
            <Blurb entry={getField(item, 'subheading')} field="blurb" />
            {planPrice && <button className={styles.ctaBtn}>{planPrice}</button>}
          </FlexTableCell>
        );
      })}
    </FlexTableRow>
  );
};
